import { useCallback, useMemo, useState } from "react";
import { useBoxBuilder } from "@/lib/store";
import { formatUsd } from "@/lib/pricing";
import { addBoxToCart } from "@/lib/api";
import { getConfig } from "@/lib/config";
import type { BoxTier, PriceBreakdown } from "@/lib/types";
import { cn } from "@/lib/cn";

interface Props {
  tier?: BoxTier;
  itemLimit: number;
  selectedCount: number;
  tissueSelected: boolean;
  breakdown: PriceBreakdown;
}

export default function SummaryBar({
  tier,
  itemLimit,
  selectedCount,
  tissueSelected,
  breakdown,
}: Props) {
  const tierVariationId = useBoxBuilder((s) => s.tierVariationId);
  const selectedItems = useBoxBuilder((s) => s.selectedItems);
  const selectedPremium = useBoxBuilder((s) => s.selectedPremium);
  const tissueId = useBoxBuilder((s) => s.tissueId);
  const reset = useBoxBuilder((s) => s.reset);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const errors = useMemo(() => {
    const out: string[] = [];
    if (!tier) out.push("Choose a box size.");
    if (itemLimit > 0 && selectedCount < itemLimit) {
      out.push(
        `Pick ${itemLimit - selectedCount} more item${itemLimit - selectedCount === 1 ? "" : "s"} to fill the box.`,
      );
    } else if (itemLimit > 0 && selectedCount > itemLimit) {
      out.push(
        `Remove ${selectedCount - itemLimit} item${selectedCount - itemLimit === 1 ? "" : "s"} to fit the box.`,
      );
    }
    if (!tissueSelected) out.push("Choose a tissue paper.");
    return out;
  }, [tier, itemLimit, selectedCount, tissueSelected]);

  const canSubmit = errors.length === 0 && !submitting;

  const submit = useCallback(async () => {
    if (!canSubmit || !tierVariationId || !tissueId) return;
    setSubmitting(true);
    setError(null);
    try {
      await addBoxToCart({
        tierVariationId,
        items: Object.entries(selectedItems).map(([id, quantity]) => ({
          id: Number(id),
          quantity,
        })),
        premium: selectedPremium,
        tissueId,
      });
      reset();
      window.location.href = getConfig().checkoutUrl;
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Couldn't add your box to the cart. Please try again.",
      );
      setSubmitting(false);
    }
  }, [
    canSubmit,
    tierVariationId,
    selectedItems,
    selectedPremium,
    tissueId,
    reset,
  ]);

  const helper =
    error ?? (errors.length > 0 ? errors[0] : null);

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex justify-center px-2 pb-2 sm:px-3 sm:pb-4">
      <div className="pointer-events-auto w-full max-w-6xl rounded-xl bg-ink-800 text-white shadow-pop ring-1 ring-black/20">
        <div className="flex items-center justify-between gap-3 p-2.5 sm:p-3">
          <div className="min-w-0">
            <div className="flex items-baseline gap-2">
              <span className="font-display text-xl font-semibold leading-none sm:text-2xl">
                {formatUsd(breakdown.totalCents)}
              </span>
              <span className="hidden truncate text-xs text-ink-300 sm:inline">
                {tier ? tier.name : "Your box"}
                {itemLimit > 0
                  ? ` \u00b7 ${selectedCount} of ${itemLimit} items`
                  : ""}
                {breakdown.premiumCents > 0
                  ? ` \u00b7 ${formatUsd(breakdown.basePriceCents)} + ${formatUsd(breakdown.premiumCents)} upgrades`
                  : ""}
              </span>
            </div>
            {helper ? (
              <div
                className={cn(
                  "mt-0.5 truncate text-xs",
                  error ? "text-red-200" : "text-brand-100",
                )}
              >
                {helper}
              </div>
            ) : itemLimit > 0 ? (
              <div className="mt-0.5 truncate text-xs text-ink-300 sm:hidden">
                {selectedCount} of {itemLimit} items
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className={cn(
              "btn shrink-0 px-4 py-2 text-sm bg-brand-500 text-white shadow-sm hover:bg-brand-400 disabled:cursor-not-allowed disabled:bg-ink-700 disabled:text-ink-300 disabled:shadow-none sm:px-5 sm:py-2.5 sm:text-base",
            )}
          >
            {submitting ? "Adding\u2026" : "Add to cart"}
          </button>
        </div>
      </div>
    </div>
  );
}
